/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const INDIGO = '#6366f1';
const VIOLET = '#8b5cf6';

const SUGGESTIONS = [
  'What free courses are available?',
  'How is my progress?',
  'Help me plan my studies',
  'What career options do I have?',
];

// Safely format a timestamp that may be a Date, ISO string, number, or undefined
// (timestamps become strings after JSON / AsyncStorage round-trips).
const formatMsgTime = (ts) => {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Local fallback responses if the AI proxy is unreachable.
const fallbackResponse = (message) => {
  const m = String(message || '').toLowerCase();
  if (m.includes('course') || m.includes('recommend')) {
    return "🎯 Based on your profile, you might enjoy:\n\n• Electrical Engineering Fundamentals\n• Advanced Project Management\n• Data Science for Engineers\n\nWant me to tell you more about any of these?";
  }
  if (m.includes('progress') || m.includes('performance')) {
    return "📊 Your learning analytics:\n\n• Improvement rate: +23% this month\n• Average score: 87%\n• Goal completion: 65%\n\nShall I create a personalized improvement plan?";
  }
  if (m.includes('help') || m.includes('stuck') || m.includes('plan')) {
    return "💡 I can help you with:\n\n• Study resources & tutorials\n• Assignment assistance\n• Exam preparation\n• Career guidance\n\nWhat would you like to focus on?";
  }
  if (m.includes('career') || m.includes('job')) {
    return "🚀 Career guidance:\n\n• Software Engineer — high demand\n• Project Manager — leadership track\n• Data Scientist — fast-growing field\n\nWould you like a personalized career roadmap?";
  }
  return "🤖 I'm here to help with courses, progress, study planning, and career guidance. Could you tell me a bit more about what you'd like to know?";
};

const AILearningAssistantScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const listRef = useRef(null);

  const [userData, setUserData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const getUserName = useCallback(() => {
    if (!userData) return 'there';
    const full = userData.full_name || userData.fullName || userData.name;
    if (full) return String(full).split(' ')[0];
    const first = userData.first_name || userData.firstName || '';
    return first || 'there';
  }, [userData]);

  useEffect(() => {
    (async () => {
      try {
        const storedUser = await AsyncStorage.getItem('user');
        const storedUserData = await AsyncStorage.getItem('userData');
        if (storedUser) setUserData(JSON.parse(storedUser));
        else if (storedUserData) setUserData(JSON.parse(storedUserData));
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  // Seed the welcome message once we know the user's name.
  useEffect(() => {
    setMessages([
      {
        id: 'welcome',
        text: `👋 Hi ${getUserName()}! I'm your AI Learning Assistant.\n\nI can help you find courses, track your progress, plan your studies, and explore career options. What would you like to know?`,
        isAI: true,
        timestamp: new Date(),
      },
    ]);
  }, [getUserName]);

  const scrollToEnd = () => {
    setTimeout(() => {
      try { listRef.current?.scrollToEnd({ animated: true }); } catch (e) {}
    }, 100);
  };

  const callAI = async (prompt) => {
    try {
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
        },
        body: JSON.stringify({
          prompt: `You are a helpful AI learning assistant for an engineering education app called JobEng Academy. The user's name is ${getUserName()}. Keep replies concise, friendly, and well-structured with short bullet points where useful. User question: ${prompt}`,
        }),
      });
      const data = await res.json();
      const text =
        data?.text ||
        data?.content ||
        data?.response ||
        data?.message ||
        data?.choices?.[0]?.message?.content;
      return (typeof text === 'string' && text.trim()) ? text.trim() : fallbackResponse(prompt);
    } catch (e) {
      return fallbackResponse(prompt);
    }
  };

  const send = async (textArg) => {
    const text = String(textArg ?? input).trim();
    if (!text) return;

    const userMsg = {
      id: `u_${Date.now()}`,
      text,
      isAI: false,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    scrollToEnd();

    const aiText = await callAI(text);
    const aiMsg = {
      id: `a_${Date.now()}`,
      text: aiText,
      isAI: true,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, aiMsg]);
    setIsTyping(false);
    scrollToEnd();
  };

  const handleClose = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('LearningDashboard');
    }
  };

  const renderMessage = ({ item }) => {
    const isAI = item.isAI;
    return (
      <View style={[styles.row, isAI ? styles.rowAI : styles.rowUser]}>
        {isAI && (
          <View style={styles.aiAvatar}>
            <MaterialCommunityIcons name="robot-happy" size={18} color="#ffffff" />
          </View>
        )}
        <View style={[styles.bubble, isAI ? styles.bubbleAI : styles.bubbleUser]}>
          <Text style={[styles.msgText, isAI ? styles.msgTextAI : styles.msgTextUser]}>
            {item.text}
          </Text>
          <Text style={[styles.msgTime, isAI ? styles.msgTimeAI : styles.msgTimeUser]}>
            {formatMsgTime(item.timestamp)}
          </Text>
        </View>
        {!isAI && (
          <View style={styles.userAvatar}>
            <Ionicons name="person" size={16} color="#ffffff" />
          </View>
        )}
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea} edges={['bottom']}>
      <StatusBar barStyle="light-content" backgroundColor={INDIGO} />

      {/* Header — top padding driven by the real safe-area inset so it always
          clears the status bar / notch on every device. */}
      <LinearGradient
        colors={[INDIGO, VIOLET]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={[styles.header, { paddingTop: insets.top + 12 }]}
      >
        <TouchableOpacity
          onPress={handleClose}
          style={styles.headerBtn}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Ionicons name="arrow-back" size={26} color="#ffffff" />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle} numberOfLines={1}>🤖 AI Learning Assistant</Text>
          <Text style={styles.headerSubtitle}>Always here to help</Text>
        </View>

        <TouchableOpacity
          onPress={() => navigation.navigate('LearningDashboard')}
          style={styles.headerBtn}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <MaterialCommunityIcons name="view-dashboard" size={24} color="#ffffff" />
        </TouchableOpacity>
      </LinearGradient>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        <FlatList
          ref={listRef}
          data={messages}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderMessage}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onContentSizeChange={scrollToEnd}
        />

        {isTyping && (
          <View style={styles.typingRow}>
            <View style={styles.aiAvatar}>
              <MaterialCommunityIcons name="robot-happy" size={18} color="#ffffff" />
            </View>
            <View style={[styles.bubble, styles.bubbleAI, styles.typingBubble]}>
              <ActivityIndicator size="small" color={INDIGO} />
              <Text style={styles.typingText}>Thinking…</Text>
            </View>
          </View>
        )}

        {/* Suggestion chips (only before the user has asked anything) */}
        {messages.length <= 1 && !isTyping && (
          <View style={styles.suggestWrap}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.suggestScroll}
            >
              {SUGGESTIONS.map((s) => (
                <TouchableOpacity key={s} style={styles.suggestChip} onPress={() => send(s)}>
                  <Text style={styles.suggestChipText}>{s}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Input bar */}
        <View style={[styles.inputBar, { paddingBottom: Math.max(insets.bottom, 10) }]}>
          <View style={styles.inputWrap}>
            <Ionicons name="chatbubble-ellipses-outline" size={20} color="#9ca3af" style={{ marginRight: 8 }} />
            <TextInput
              style={styles.input}
              placeholder="Ask me anything about your learning…"
              placeholderTextColor="#9ca3af"
              value={input}
              onChangeText={setInput}
              multiline
              onSubmitEditing={() => send()}
            />
          </View>
          <TouchableOpacity
            style={[styles.sendBtn, !input.trim() && styles.sendBtnDisabled]}
            onPress={() => send()}
            disabled={!input.trim() || isTyping}
          >
            <LinearGradient colors={[INDIGO, VIOLET]} style={styles.sendGradient}>
              <Ionicons name="send" size={20} color="#ffffff" />
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  headerBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.85)',
    marginTop: 2,
  },
  listContent: {
    padding: 16,
    paddingBottom: 8,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 14,
    alignItems: 'flex-end',
  },
  rowAI: {
    justifyContent: 'flex-start',
  },
  rowUser: {
    justifyContent: 'flex-end',
  },
  aiAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: INDIGO,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  userAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: VIOLET,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  bubble: {
    maxWidth: '78%',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  bubbleAI: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 1,
  },
  bubbleUser: {
    backgroundColor: INDIGO,
    borderTopRightRadius: 4,
  },
  msgText: {
    fontSize: 15,
    lineHeight: 21,
  },
  msgTextAI: {
    color: '#1f2937',
  },
  msgTextUser: {
    color: '#ffffff',
  },
  msgTime: {
    fontSize: 10,
    marginTop: 4,
    alignSelf: 'flex-end',
  },
  msgTimeAI: {
    color: '#9ca3af',
  },
  msgTimeUser: {
    color: 'rgba(255,255,255,0.8)',
  },
  typingRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    marginBottom: 10,
  },
  typingBubble: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  typingText: {
    marginLeft: 8,
    color: '#6b7280',
    fontSize: 13,
  },
  suggestWrap: {
    paddingBottom: 6,
  },
  suggestScroll: {
    paddingHorizontal: 12,
  },
  suggestChip: {
    backgroundColor: '#eef2ff',
    borderWidth: 1,
    borderColor: '#c7d2fe',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 9,
    marginHorizontal: 4,
  },
  suggestChipText: {
    color: INDIGO,
    fontSize: 13,
    fontWeight: '500',
  },
  inputBar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 12,
    paddingTop: 10,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  inputWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    borderRadius: 22,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 10 : 4,
    maxHeight: 120,
  },
  input: {
    flex: 1,
    fontSize: 15,
    color: '#1f2937',
    maxHeight: 100,
  },
  sendBtn: {
    marginLeft: 8,
    borderRadius: 22,
    overflow: 'hidden',
  },
  sendBtnDisabled: {
    opacity: 0.5,
  },
  sendGradient: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default AILearningAssistantScreen;
